"use client";

import { motion } from "src/lib/motion";
import { cn } from "@/lib/utils";
import Section from "@/components/section";
import { Icons } from "@/components/icons";
import BlurFade from "@/components/magicui/blur-fade";
import Image from "next/image";
import { Globe } from "lucide-react";
import { useTranslations } from "@/lib/hooks/use-translations";
import chrisImage from "@/assets/images/people/chris.jpg";
import MarcoImage from "@/assets/images/people/Marco.jpg";

interface TeamMemberProps {
  name: string;
  role: string;
  image: string;
  bio: string;
  expertise: string[];
  className?: string;
  objectPosition?: string;
}

interface AIConsultingTeamProps {
  locale: string;
}

function TeamMember({ name, role, image, bio, expertise, className, objectPosition = "center" }: TeamMemberProps) {
  return (
    <div className={cn("text-center group", className)}>
      <div className="relative overflow-hidden rounded-xl mb-6 aspect-[4/3] shadow-lg">
        <Image
          src={image}
          alt={name}
          className="object-cover w-full h-full transition-all duration-500"
          style={{ objectPosition }}
          width={500}
          height={375}
        />
      </div>
      <h3 className="font-semibold text-2xl mb-2">{name}</h3>
      <p className="text-primary font-medium text-lg mb-4">{role}</p>
      <p className="text-muted-foreground text-lg max-md:text-center mb-6 max-w-xl mx-auto leading-relaxed">{bio}</p>
      <div className="flex flex-wrap justify-center gap-2">
        {expertise.map((skill, index) => (
          <span
            key={index}
            className="px-4 py-1.5 pb-[7px] bg-primary/10 text-primary rounded-full text-sm font-medium"
          >
            {skill}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function AIConsultingTeam({ locale }: AIConsultingTeamProps) {
  const { t } = useTranslations();

  // Image mapping for team members
  const imageMap = {
    "Chris Meitner": { src: chrisImage.src, objectPosition: "center 35%" },
    "Marco Langenfeld": { src: MarcoImage.src, objectPosition: "85% center" }
  };

  // Get team members from translations
  const teamMembersData = t('pages.about.team.members') as Array<{
    name: string;
    role: string;
    bio: string;
    expertise: string[];
  }>;

  const teamMembers = teamMembersData.map((member) => ({
    ...member,
    image: imageMap[member.name as keyof typeof imageMap]?.src || chrisImage.src,
    objectPosition: imageMap[member.name as keyof typeof imageMap]?.objectPosition || "center"
  }));

  return (
    <Section
      id="ai-consulting-team"
      className="bg-muted/30"
      mainCta={{
        text: t('pages.about.team.buttons.getStarted'),
        href: locale === 'de' ? "/de/kontakt" : locale === 'zh-CN' ? "/zh-CN/contact" : "/contact",
        icon: <Icons.logoIcon className="h-6 w-6" />
      }}
      secondaryCta={{
        text: t('pages.about.team.buttons.readMore'),
        onClick: () => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })
      }}
    >
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-4 break-words"
          >
            {t('pages.about.team.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-muted-foreground max-w-2xl mx-auto text-lg"
          >
            {t('pages.about.team.subtitle')}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16 max-w-6xl mx-auto">
          {teamMembers.map((member, index) => (
            <BlurFade key={index} delay={0.1 * index} inView>
              <TeamMember
                name={member.name}
                role={member.role}
                image={member.image}
                bio={member.bio}
                expertise={member.expertise}
                objectPosition={member.objectPosition}
              />
            </BlurFade>
          ))}
        </div>

        <BlurFade delay={0.1 * teamMembers.length + 0.1} inView>
          <div className="mt-16 text-center border-t border-border/50 pt-8 max-w-3xl mx-auto">
            <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full mb-4">
              <Globe className="h-6 w-6 text-primary" />
            </div>
            <p className="text-lg text-muted-foreground max-md:text-center">
              {t('pages.about.team.network.text')}
            </p>
          </div>
        </BlurFade>
    </Section>
  );
}
